import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant =
	| "primary"
	| "secondary"
	| "ghost"
	| "danger"
	| "outline";

export type ButtonSize = "sm" | "md" | "lg";

const variantClass: Record<ButtonVariant, string> = {
	primary: "btn-primary border-0 text-primary-foreground",
	secondary: "btn-secondary",
	ghost:
		"min-h-touch rounded-xl bg-transparent px-3 font-semibold text-foreground hover:bg-muted",
	danger:
		"min-h-touch rounded-xl bg-danger px-4 font-semibold text-white hover:opacity-90",
	outline:
		"min-h-touch rounded-xl border border-border bg-card px-4 font-semibold hover:border-primary/40",
};

const sizeClass: Record<ButtonSize, string> = {
	sm: "!min-h-10 px-3 text-xs gap-1.5",
	md: "px-4 text-sm gap-2",
	lg: "px-6 text-base gap-2",
};

export function Button({
	variant = "primary",
	size = "md",
	fullWidth,
	loading,
	iconLeft,
	iconRight,
	className,
	children,
	disabled,
	type = "button",
	...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
	variant?: ButtonVariant;
	size?: ButtonSize;
	fullWidth?: boolean;
	loading?: boolean;
	iconLeft?: ReactNode;
	iconRight?: ReactNode;
}) {
	return (
		<button
			type={type}
			disabled={disabled || loading}
			className={cn(
				"inline-flex items-center justify-center whitespace-nowrap disabled:pointer-events-none disabled:opacity-50",
				variantClass[variant],
				sizeClass[size],
				fullWidth && "w-full",
				className,
			)}
			{...rest}
		>
			{loading ? (
				<Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
			) : (
				iconLeft
			)}
			{children}
			{!loading && iconRight}
		</button>
	);
}
